import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Share2, Calendar, Clock, User, ArrowLeft, ArrowUpRight, AlertTriangle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { CtaBanner } from '../components/site/BottomSections';
import { getPost, relatedPosts, wpImage, wpAuthor, wpDate, wpReadTime, wpCategories, wpExcerpt } from '../lib/wp';

function Skeleton() {
  return (
    <div className="animate-pulse min-h-screen bg-[#0f1a14] pt-32 px-6">
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="h-4 w-40 bg-white/10" />
        <div className="h-14 w-3/4 bg-white/10" />
        <div className="h-4 w-1/2 bg-white/10" />
        <div className="h-96 w-full bg-white/10 mt-8" />
      </div>
    </div>
  );
}

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setPost(null);
    setRelated([]);
    (async () => {
      try {
        const p = await getPost(slug);
        if (cancelled) return;
        if (!p) {
          setError('This article could not be found.');
          return;
        }
        setPost(p);
        try {
          const rel = await relatedPosts(p, 3);
          if (!cancelled) setRelated(rel);
        } catch (_) {
          /* ignore */
        }
      } catch (err) {
        if (!cancelled) setError('Could not load the article. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const share = async () => {
    if (!post) return;
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: post.title.rendered, text: wpExcerpt(post), url }); } catch (_) {}
    } else {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied', description: 'Share it with anyone.' });
    }
  };

  const heroImg = useMemo(() => (post ? wpImage(post) : null), [post]);

  if (loading) return <Skeleton />;

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#0f1a14] pt-32 pb-24 px-6 grid place-items-center">
        <div className="max-w-md text-center">
          <AlertTriangle className="h-10 w-10 text-[#c9a56a] mx-auto" />
          <h1 className="font-serif text-3xl text-white mt-4">Article unavailable</h1>
          <p className="text-white/60 mt-2">{error || 'This article could not be loaded.'}</p>
          <button onClick={() => navigate('/blog')} className="mt-8 bg-[#c9a56a] text-[#0f1a14] px-6 py-3 tracking-widest text-xs uppercase">Back to Journal</button>
        </div>
      </div>
    );
  }

  const cats = wpCategories(post);
  const author = wpAuthor(post);

  return (
    <div>
      <section className="relative h-[70vh] min-h-[500px] overflow-hidden bg-[#0f1a14]">
        {heroImg && (
          <img src={heroImg} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1a14]/40 via-transparent to-[#0f1a14]/95" />
        <div className="relative mx-auto max-w-4xl h-full px-6 lg:px-10 flex flex-col justify-end pb-16">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#c9a56a] text-xs uppercase tracking-widest mb-6 hover:text-white transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Journal
          </Link>
          {cats[0] && (
            <span className="bg-[#c9a56a] text-[#0f1a14] px-3 py-1 text-[10px] uppercase tracking-widest self-start">{cats[0]}</span>
          )}
          <h1
            className="font-serif text-white text-4xl md:text-6xl leading-[1.05] mt-4"
            dangerouslySetInnerHTML={{ __html: post.title.rendered }}
          />
          <div className="flex flex-wrap items-center gap-5 mt-6 text-white/70 text-xs uppercase tracking-widest">
            <span className="flex items-center gap-1"><User className="h-3 w-3" /> {author}</span>
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {wpDate(post)}</span>
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {wpReadTime(post)} read</span>
          </div>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          {/* {post.excerpt?.rendered && (
            <div
              className="font-serif text-2xl text-[#0f1a14]/80 italic leading-relaxed wp-excerpt"
              dangerouslySetInnerHTML={{ __html: post.excerpt.rendered }}
            />
          )} */}
                    {/* <img src={heroImg} alt="" className="inset-0 h-full w-full" /> */}
          {/* <div className="h-px w-16 bg-[#8a6b3a] my-10" /> */}
          <article
            className="wp-content text-[#0f1a14]/85 text-lg leading-[1.85]"
            dangerouslySetInnerHTML={{ __html: post.content.rendered }}
          />
          <div className="mt-14 pt-8 border-t border-[#0f1a14]/10 flex items-center justify-between flex-wrap gap-3">
            <div className="text-[#0f1a14]/60 text-sm">Written by <span className="text-[#0f1a14]">{author}</span></div>
            <button onClick={share} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest border border-[#0f1a14]/20 hover:border-[#8a6b3a] hover:text-[#8a6b3a] px-4 py-2 transition-colors">
              <Share2 className="h-3.5 w-3.5" /> Share Article
            </button>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-[#f6f1e7] py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
              <h2 className="font-serif text-3xl md:text-4xl text-[#0f1a14]">Keep reading</h2>
              <Link to="/blog" className="inline-flex items-center gap-1 text-[#8a6b3a] hover:text-[#0f1a14] text-xs uppercase tracking-widest">
                All articles <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {related.map((b) => {
                const img = wpImage(b);
                return (
                  <Link key={b.id} to={`/blog/${b.slug}`} className="group">
                    <div className="relative aspect-[4/3] overflow-hidden bg-white">
                      {img && (
                        <img src={img} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      )}
                    </div>
                    <div className="mt-5">
                      <div className="text-xs uppercase tracking-widest text-[#8a6b3a]">{wpDate(b)} · {wpReadTime(b)} read</div>
                      <h3
                        className="font-serif text-xl text-[#0f1a14] mt-3 leading-snug group-hover:text-[#8a6b3a] transition-colors"
                        dangerouslySetInnerHTML={{ __html: b.title.rendered }}
                      />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <CtaBanner />
    </div>
  );
}
